import { Box, CircularProgress } from '@mui/material';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { FIRST_RUN_PATH, hasSeenWorkerFirstRun } from '../lib/workerFirstRun';

import { useAuth } from './AuthContext';
import { homeForRole } from './roleHome';
import { isSignedIn } from './signedIn';

const ALLOWED: ReadonlyArray<string> = ['worker', 'admin'];

export function RequireWorker({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // An anonymous guest session (/order) is not an account — sign in first.
  if (!isSignedIn(user)) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (!user.role || !ALLOWED.includes(user.role)) {
    return <Navigate to={homeForRole(user.role)} replace />;
  }

  // UX 1A-e: a worker's first visit goes through the first-run page once.
  // Admins previewing the worker portal never do. The page marks itself
  // seen on mount, so this fires once per account on this device.
  if (
    user.role === 'worker' &&
    location.pathname !== FIRST_RUN_PATH &&
    !hasSeenWorkerFirstRun(user.uid)
  ) {
    return <Navigate to={FIRST_RUN_PATH} replace state={{ from: location }} />;
  }

  return <>{children}</>;
}
